import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, CheckCheck, Loader2, Archive, MoreVertical } from 'lucide-react';
import { useLanguage } from '@/context/language-context';
import { usePresence } from '@/context/presence-context';
import { Conversation } from '@/hooks/useConversations';
import { format, isToday, isYesterday } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ConversationsListProps {
  conversations: Conversation[];
  isLoading: boolean;
  activeConversationId?: string | null;
  onSelectConversation: (conversationId: string) => void;
  onArchiveConversation?: (conversationId: string) => Promise<void>;
}

const ConversationsList: React.FC<ConversationsListProps> = ({ 
  conversations, 
  isLoading,
  activeConversationId,
  onSelectConversation,
  onArchiveConversation
}) => {
  const { t, currentLanguage } = useLanguage();
  const [searchText, setSearchText] = React.useState('');
  const { onlineUsers } = usePresence();

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const locale = currentLanguage === 'fr' ? fr : enUS;
    
    if (isToday(date)) {
      return format(date, 'HH:mm', { locale });
    } else if (isYesterday(date)) {
      return t('Yesterday');
    } else {
      return format(date, 'dd/MM/yyyy', { locale });
    }
  };

  const filteredConversations = conversations.filter(conv => {
    const partner = conv.partner;
    if (!partner) return false;
    const searchTerm = searchText.toLowerCase();
    return (
      partner.name.toLowerCase().includes(searchTerm) ||
      (partner.type === 'escort' && partner.city?.toLowerCase().includes(searchTerm))
    );
  });

  const handleArchive = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation(); // Empêche le déclenchement de onSelectConversation
    if (onArchiveConversation) {
      onArchiveConversation(conversationId);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="p-3 border-b border-gray-100">
        <div className="relative mb-3">
          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder={t('messages.search_placeholder') || 'Rechercher par nom, ville...'}
            className="pl-8 bg-gray-50 border-gray-200 rounded-full h-9"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      </div>
      
      <ScrollArea className="flex-1 conversation-list-scroll">
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-6 w-6 text-pink-500 animate-spin" />
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500 mt-4">
            {searchText ? t('search.no_results') : t('messages.no_conversations')}
          </div>
        ) : (
          filteredConversations.map((conversation) => {
            const partner = conversation.partner;
            const sublineText = partner?.type === 'escort' && partner?.city 
                               ? `${partner.city} · ${conversation.last_message || t('messages.no_messages_yet')}`
                               : conversation.last_message || t('messages.no_messages_yet');
            return (
              <div
                key={conversation.id}
                className={cn(
                  "p-3 flex items-center gap-3 hover:bg-pink-50 cursor-pointer transition-colors border-b border-gray-100 group",
                  activeConversationId === conversation.id ? 'bg-pink-50 border-l-4 border-pink-500' : 'border-l-4 border-transparent'
                )}
                onClick={() => onSelectConversation(conversation.id)}
              >
                <Avatar className="h-11 w-11 border">
                  <AvatarImage
                    src={conversation.partner?.avatar || undefined}
                    alt={conversation.partner?.name || "User"}
                  />
                  <AvatarFallback className="bg-gray-100">
                    {conversation.partner?.name.slice(0, 1).toUpperCase() || "U"}
                  </AvatarFallback>
                  {conversation.partner?.id && onlineUsers.has(conversation.partner.id) && (
                    <span className="absolute bottom-0 right-0 block h-3 w-3 rounded-full bg-green-500 ring-2 ring-white" />
                  )}
                </Avatar>
                
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center">
                    <div className="font-semibold text-sm text-gray-800 truncate pr-2">
                      {conversation.partner?.name}
                    </div>
                    <div className="text-xs text-gray-500 whitespace-nowrap">
                      {formatDate(conversation.last_message_time)}
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center mt-1">
                    <div className="text-xs text-gray-500 truncate pr-2 max-w-[180px]">
                      {sublineText}
                    </div>
                    
                    <div className="flex items-center">
                      {conversation.unreadCount > 0 && (
                        <Badge variant="destructive" className="text-[10px] h-4 min-w-4 px-1 flex items-center justify-center rounded-full">
                          {conversation.unreadCount}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                {onArchiveConversation && (
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-gray-400 hover:text-gray-600 hover:bg-pink-100"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        <DropdownMenuItem 
                          className="cursor-pointer flex items-center gap-2 text-gray-600"
                          onClick={(e) => handleArchive(e, conversation.id)}
                        >
                          <Archive className="h-4 w-4" />
                          <span>{t('messages:messages.archive') || 'Archiver'}</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            );
          })
        )}
      </ScrollArea>
    </div>
  );
};

export default ConversationsList;
